import { MessageModel } from "common247";
import { BrandColors } from "@/constants/themes/base";
import type { IKebabActionItem } from "@/components/ThemedKebabMenu";

/**
 * Brand-aligned theme configuration for each message type.
 */
export const MESSAGE_TYPE_THEME: Record<string, { color: string; bg: string; border: string }> = {
  General: {
    color: "#4A608F", // Archival Blue Linen
    bg: "rgba(161, 174, 209, 0.16)",
    border: "#BAC4DF",
  },
  System: {
    color: "#161F36", // Deep Archival Ink
    bg: "rgba(22, 31, 54, 0.08)",
    border: "#CBD5E1",
  },
  Alert: {
    color: BrandColors.beetRed.hex, // Beet Red
    bg: "rgba(171, 23, 23, 0.08)",
    border: "rgba(171, 23, 23, 0.28)",
  },
  Warning: {
    color: "#B45309", // Archival Amber
    bg: "rgba(255, 242, 77, 0.22)", // Lemon Yellow Wash
    border: "rgba(180, 83, 9, 0.28)",
  },
  Feedback: {
    color: BrandColors.sungoldOrange.hex, // Sungold Orange
    bg: "rgba(255, 91, 3, 0.08)",
    border: "rgba(255, 91, 3, 0.28)",
  },
  Task: {
    color: "#059669", // Forest Emerald
    bg: "rgba(5, 150, 105, 0.12)",
    border: "rgba(5, 150, 105, 0.3)",
  },
};

/**
 * Resolves the human-readable recipient display label for a message.
 * Never returns a raw contactId.
 */
export function resolveMessageRecipientLabel(message: MessageModel, isGroup: boolean): string {
  if (message.recipientDisplayName?.trim()) {
    return message.recipientDisplayName.trim();
  }
  if (message.targetName?.trim()) {
    return message.targetName.trim();
  }
  if (message.recipients && message.recipients.length > 0) {
    if (isGroup) {
      const groupName = message.recipients.find((r) => Boolean(r.groupName))?.groupName;
      return groupName ? groupName : `Group (${message.recipients.length} members)`;
    }
    const names = message.recipients.map((r) => r.displayName?.trim()).filter(Boolean);
    if (names.length > 0) {
      return names.join(", ");
    }
    if (message.recipients.length === 1) {
      return "Recipient";
    }
    return `${message.recipients.length} recipients`;
  }
  return "Recipient";
}

export interface IBuildMessageKebabOptions {
  message: MessageModel;
  isUnread: boolean;
  canMarkReadState: boolean;
  isTrashed: boolean;
  onPress: (message: MessageModel) => void;
  onMarkRead?: (messageId: string) => void;
  onMarkUnread?: (messageId: string) => void;
  onReply?: (message: MessageModel) => void;
  onDelete?: (messageId: string) => void;
  onMoveToTrash?: (messageId: string) => void;
  onRestore?: (messageId: string) => void;
}

/**
 * Builds the kebab action menu items for a message card based on state and permissions.
 */
export function buildMessageKebabActions({
  message,
  isUnread,
  canMarkReadState,
  isTrashed,
  onPress,
  onMarkRead,
  onMarkUnread,
  onReply,
  onDelete,
  onMoveToTrash,
  onRestore,
}: IBuildMessageKebabOptions): IKebabActionItem[] {
  const kebabActions: IKebabActionItem[] = [
    {
      id: "view",
      label: "View Message",
      icon: "eye-outline",
      onPress: () => onPress(message),
    },
  ];

  if (canMarkReadState) {
    if (isUnread && onMarkRead) {
      kebabActions.push({
        id: "mark-read",
        label: "Mark as Read",
        icon: "mail-open-outline",
        onPress: () => onMarkRead(message.id),
      });
    } else if (!isUnread && onMarkUnread) {
      kebabActions.push({
        id: "mark-unread",
        label: "Mark as Unread",
        icon: "mail-unread-outline",
        onPress: () => onMarkUnread(message.id),
      });
    }
  }

  if (onReply && !isTrashed) {
    kebabActions.push({
      id: "reply",
      label: "Reply",
      icon: "arrow-undo-outline",
      onPress: () => onReply(message),
    });
  }

  if (isTrashed) {
    if (onRestore) {
      kebabActions.push({
        id: "restore",
        label: "Restore Message",
        icon: "refresh-outline",
        onPress: () => onRestore(message.id),
      });
    }
    if (onDelete) {
      kebabActions.push({
        id: "delete",
        label: "Delete Permanently",
        icon: "trash-outline",
        destructive: true,
        onPress: () => onDelete(message.id),
      });
    }
  } else {
    if (onMoveToTrash) {
      kebabActions.push({
        id: "trash",
        label: "Move to Trash",
        icon: "trash-outline",
        destructive: true,
        onPress: () => onMoveToTrash(message.id),
      });
    } else if (onDelete) {
      kebabActions.push({
        id: "delete",
        label: "Delete Message",
        icon: "trash-outline",
        destructive: true,
        onPress: () => onDelete(message.id),
      });
    }
  }

  return kebabActions;
}
